import { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * The two catalogue reads the calendar needs: where lessons happen, and what
 * kinds of lesson exist.
 *
 * Together in one file because they are the same question asked twice — what
 * this business offers — and both are read once when the calendar opens.
 */

export interface Facility {
  facility_id: string;
  name: string;
  city: string | null;
}

/** A lesson product: a name and how many people it holds. Configuration, not code. */
export interface Product {
  tier_id: string;
  name: string;
  capacity: number;
}

export interface Service {
  service_id: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  country: string;
  /** An Assessment, not a lesson (032). */
  is_assessment: boolean;
  products: Product[];
}

type Loaded<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'failed'; message: string };

function useRead<T>(path: string, pick: (body: never) => T): Loaded<T> {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' });

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    setState({ status: 'loading' });

    apiFetch<never>(path, getToken)
      .then((body) => {
        if (!cancelled) setState({ status: 'ready', value: pick(body) });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) return;
        setState({
          status: 'failed',
          message:
            cause instanceof ApiError
              ? cause.message
              : 'The API did not answer. Is the Functions host running on port 7071?',
        });
      });

    return () => {
      cancelled = true;
    };
    // `pick` is written inline at each call site and would be a new function
    // every render; the path is what decides whether to fetch again.
  }, [isAuthenticated, getToken, path]);

  return state;
}

/** Only places with hours you could book. A facility with none is a dead end. */
export function useFacilities(): Loaded<Facility[]> {
  return useRead<Facility[]>('/api/facilities', (body: never) => (body as { facilities: Facility[] }).facilities);
}

export function useServices(): Loaded<Service[]> {
  return useRead<Service[]>('/api/services', (body: never) => (body as { services: Service[] }).services);
}
